<script setup>
import { computed } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import SwiperCore, { Navigation, Autoplay } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import '../../styles/configurator-section.css';

const staticImagesBaseUrl = process.env.VUE_APP_DS_STATIC_IMAGES_BASE_URL;

const emit = defineEmits(['select']);

SwiperCore.use([Navigation, Autoplay]);

const props = defineProps({
  items: {
    type: Object,
    default: null,
  },
});

const carousel = computed(() => {
  if (!props.items) return null;
  return props.items.find(x => x.id === 'carousel');
});

const showNavigation = computed(() => carousel?.value?.options?.length > 3);

const breakpoints = {
  0: { slidesPerView: 1, spaceBetween: 14 },
  401: { slidesPerView: 1, spaceBetween: 14 },
  441: { slidesPerView: 1.1, spaceBetween: 14 },
  481: { slidesPerView: 1.2, spaceBetween: 15 },
  561: { slidesPerView: 1.4, spaceBetween: 15 },
  641: { slidesPerView: 1.6, spaceBetween: 16 },
  721: { slidesPerView: 1.8, spaceBetween: 16 },
  801: { slidesPerView: 2, spaceBetween: 18 },
  921: { slidesPerView: 2.2, spaceBetween: 18 },
  1025: { slidesPerView: 2.5, spaceBetween: 20 },
  1101: { slidesPerView: 2.8, spaceBetween: 20 },
  1201: { slidesPerView: 3, spaceBetween: 20 },
  1295: { slidesPerView: 2.8, spaceBetween: 20 },
  1441: { slidesPerView: 3.5, spaceBetween: 24 },
};
</script>

<template>
  <section
    v-if="props.items"
    id="highlights"
    class="configurator-section"
  >
    <h2>{{ $i18n('highlightsHeaderTitle') }}</h2>
    <div
      v-if="carousel"
      class="highlights__carousel"
    >
      <swiper
        class="highlights-swiper"
        :loop="carousel.options.length > 3"
        :breakpoints="breakpoints"
        :navigation="showNavigation"
        :autoplay="{
          delay: 5000,
          disableOnInteraction: false,
        }"
      >
        <swiper-slide
          v-for="item in carousel.options"
          :key="item.id"
        >
          <article class="highlight-card">
            <div class="highlight-card__header">
              <h3 class="highlight-card__title">
                {{ $i18n(item.value) }}
              </h3>
            </div>
            <figure class="highlight-card__media">
              <img
                class="highlight-card__img"
                :src="`${staticImagesBaseUrl}/${item.descriptionImage}`"
                :alt="$i18n(item.value)"
                loading="lazy"
                decoding="async"
              >
            </figure>
            <div class="highlight-card__body">
              <p class="highlight-card__desc">
                {{ $i18n(item.description) }}
              </p>
            </div>
          </article>
        </swiper-slide>
      </swiper>
    </div>
  </section>
</template>

<style scoped>
  /* Carousel container ensures arrows sit correctly */
  .highlights__carousel {
    position: relative;
    width: 80vw;
    max-width: 80vw;
  }

  /* Card */
  .highlight-card {
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid var(--sdds-grey-300, #e0e0e0);
    border-radius: 5px;
    overflow: hidden;
    height: 100%;
  }

  /* Image area: keeps a consistent height, crops nicely */
  .highlight-card__media {
    margin: 0;
    height: 220px;
    overflow: hidden;
    background: var(--sdds-grey-100, #f6f6f6);
  }

  .highlight-card__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  /* Body */
  .highlight-card__header {
    height: 80px;
  }

  .highlight-card__body {
    height: 130px;
  }

  .highlight-card__header, .highlight-card__body {
    padding: 16px;
  }

  .highlight-card__title {
    margin: 0 0 8px 0;
    font-weight: 600;
  }

  .highlight-card__desc {
    margin: 0;
    line-height: 1.45;
    color: var(--sdds-grey-700, #4f4f4f);
  }

  /* Swiper arrows — use :deep() to reach into child component when scoped */
  :deep(.swiper-button-prev),
  :deep(.swiper-button-next) {
    width: 40px;
    height: 40px;
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid var(--sdds-grey-300, #e0e0e0);
    box-shadow: 0 2px 10px rgba(0,0,0,0.08);
    color: grey;
  }

  /* Swiper uses an icon font here */
  :deep(.swiper-button-prev::after),
  :deep(.swiper-button-next::after) {
    font-size: 18px;
  }

  :deep(.swiper-button-disabled) {
    opacity: 0.4;
    cursor: default;
  }

  /* subtle breathing room around slides */
  /* :deep(.swiper) {
    padding: 4px 8px 24px;
  } */

  @media (max-width: 1600px) {
    .highlight-card__desc {
      line-height: 1.2;
    }
  }

  @media (max-width: 1294px) {
    .highlights__carousel {
      width: 100vw;
    }
  }

  @media (max-width: 1200px) {
    .highlight-card__title {
      font-size: 22px;
    }
  }

  @media (max-width: 1060px) {
    .highlight-card__title {
      font-size: 21px;
    }
  }

  @media (min-width: 1024px) {
    .highlight-card__media {
      height: 260px;
    }
  }

  @media (max-width: 1000px) {
    .highlight-card__desc {
      font-size: 1em;
    }
  }

  @media (max-width: 830px) {
    .highlight-card__title {
      font-size: 20.4px;
    }
  }

  @media (max-width: 830px) {
    .highlight-card__title {
      font-size: 20.4px;
    }
  }

  @media (max-width: 740px) {
    .highlight-card__title {
      font-size: 20px;
    }
  }

  @media (max-width: 680px) {
    .highlight-card__title {
      font-size: 18px;
    }
  }

  @media (max-width: 500px) {
    .highlight-card__title {
      line-height: 18px;
    }
    .highlight-card__desc {
      font-size: 0.9em;
    }
  }

  @media (max-width: 440px) {
    .highlight-card__title {
      font-size: 16px;
    }
    .highlight-card__desc {
      font-size: 0.8em;
    }
  }

  @media (max-width: 380px) {
    .highlight-card__title {
      font-size: 14px;
    }
    .highlight-card__desc {
      font-size: 0.8em;
    }
  }
</style>
